<template>
  <div class="ev-simulate-page" ref="pageRoot">
    <div class="top-bar">
      <el-form size="mini" :inline="true" class="compact-form">
        <el-form-item label="开始日期">
          <el-date-picker v-model="param.startDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期"
            style="width: 130px" :disabled="running"></el-date-picker>
        </el-form-item>
        <el-form-item label="连续天数">
          <el-input-number v-model="param.days" :min="1" :max="30" :controls="false" style="width: 56px"
            :disabled="running"></el-input-number>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="mini" :loading="running" @click="startSimulate">开始推导</el-button>
        </el-form-item>
      </el-form>

      <div class="top-bar__progress" v-if="taskId && task && task.status === 0">
        <span class="status-text">{{ progressText }}</span>
        <el-progress :percentage="progressPercent" :stroke-width="6" :show-text="false"
          class="inline-progress"></el-progress>
        <span class="progress-pct">{{ progressPercent }}%</span>
      </div>

      <div class="top-bar__summary" v-if="showResult">
        <span class="summary-tag">求解 {{ solveStatus }}</span>
        <span class="summary-tag">已派 {{ filteredAssignedPerson }} 人</span>
        <span class="summary-tag">未派 {{ filteredUnassignedPerson }} 人</span>
        <span class="summary-tag">行程 {{ filteredTripCount }} 个</span>
      </div>

      <span class="top-bar__error" v-if="task && task.status === 2">{{ task.errorMsg }}</span>
      <span class="top-bar__done" v-else-if="taskId && task && task.status === 1">推导完成</span>
    </div>

    <div class="main-content" v-if="showResult">
      <div class="tabs-with-filter">
        <el-form size="mini" :inline="true" class="compact-form tab-filters">
          <el-form-item label="路线">
            <el-select v-model="filterPathId" placeholder="全部路线" clearable style="width: 130px">
              <el-option v-for="item in pathOpts" :key="item.id" :label="item.name" :value="item.id"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="日期">
            <el-select v-model="selectedDayIndex" style="width: 115px">
              <el-option v-for="(item, index) in dayOptions" :key="index" :label="item.label"
                :value="index"></el-option>
            </el-select>
          </el-form-item>
        </el-form>
        <el-tabs v-model="tab" class="fill-tabs">
          <el-tab-pane name="unassigned">
            <span slot="label">已订（{{ filteredUnassignedPerson }}人）</span>
            <unassigned-order ref="unassignedRef" :list="filteredUnassignedOrders" :date="currentDayResult.date"
              :table-height="tableHeight" />
          </el-tab-pane>
          <el-tab-pane name="dispatched">
            <span slot="label">已派（{{ filteredAssignedPerson }}人）</span>
            <simulated-dispatched-order ref="dispatchedRef" :trips="filteredDispatchedTrips"
              :table-height="tableHeight" />
          </el-tab-pane>
          <el-tab-pane name="idle">
            <span slot="label">空闲运力（{{ peakIdleVehicleCount }}车 {{ peakIdleLeftSeats }}座）</span>
            <idle-vehicle-panel ref="idleRef" :slots="filteredIdleSlotSnapshots" :table-height="tableHeight" />
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import { submitEvSimulate, getEvSimulateTask } from "@/api/ev_solver";
import { DateUtil } from "@/util";
import { mapState } from "vuex";
import UnassignedOrder from "./UnassignedOrder";
import SimulatedDispatchedOrder from "./SimulatedDispatchedOrder";
import IdleVehiclePanel from "./IdleVehiclePanel";

export default {
  components: {
    UnassignedOrder,
    SimulatedDispatchedOrder,
    IdleVehiclePanel
  },
  data() {
    return {
      tab: "dispatched",
      param: {
        startDate: DateUtil.getDayStr(0, "yyyy-MM-dd"),
        days: 3
      },
      taskId: null,
      task: null,
      running: false,
      pollTimer: null,
      selectedDayIndex: 0,
      filterPathId: null,
      tableHeight: 480
    };
  },
  computed: {
    ...mapState({
      pathOpts: (state) => state.pathList
    }),
    showResult() {
      return !!(this.currentDayResult && this.task && this.task.status === 1);
    },
    solveStatus() {
      const summary = this.currentDayResult && this.currentDayResult.solveSummary;
      return (summary && summary.status) || "-";
    },
    prevDateHint() {
      if (!this.param.startDate) return "";
      return DateUtil.getSpecDayStr(this.param.startDate, -1, "yyyy-MM-dd");
    },
    dayOptions() {
      if (!this.task || !this.task.dayResults) return [];
      return this.task.dayResults.map((item, index) => ({
        label: item.date,
        value: index
      }));
    },
    currentDayResult() {
      if (!this.task || !this.task.dayResults || !this.task.dayResults.length) return null;
      const idx = Math.min(this.selectedDayIndex, this.task.dayResults.length - 1);
      return this.task.dayResults[idx];
    },
    filteredUnassignedOrders() {
      const list = (this.currentDayResult && this.currentDayResult.unassignedOrders) || [];
      if (!this.filterPathId) return list;
      return list.filter((item) => item.pathId === this.filterPathId);
    },
    filteredDispatchedTrips() {
      const list = (this.currentDayResult && this.currentDayResult.dispatchedTrips) || [];
      if (!this.filterPathId) return list;
      return list.filter((item) => item.pathId === this.filterPathId);
    },
    filteredAssignedPerson() {
      return this.filteredDispatchedTrips.reduce((sum, trip) => sum + (trip.assignedSeats || 0), 0);
    },
    filteredUnassignedPerson() {
      return this.filteredUnassignedOrders.reduce((sum, item) => sum + (item.number || 0), 0);
    },
    filteredTripCount() {
      return this.filteredDispatchedTrips.length;
    },
    filteredIdleSlotSnapshots() {
      const slots = (this.currentDayResult && this.currentDayResult.idleSlotSnapshots) || [];
      if (!this.filterPathId) return slots;
      const path = this.pathOpts.find((item) => item.id === this.filterPathId);
      if (!path) return slots;
      const areaIds = [path.startArea, path.endArea].filter(Boolean);
      return slots.map((slot) => {
        const vehicles = (slot.vehicles || []).filter((v) => areaIds.includes(v.areaId));
        const totalLeftSeats = vehicles.reduce((sum, v) => sum + (v.leftSeats || 0), 0);
        return {
          ...slot,
          vehicles,
          vehicleCount: vehicles.length,
          totalLeftSeats
        };
      });
    },
    peakIdleVehicleCount() {
      return this.filteredIdleSlotSnapshots.reduce(
        (max, item) => Math.max(max, item.vehicleCount || 0),
        0
      );
    },
    peakIdleLeftSeats() {
      const peak = this.filteredIdleSlotSnapshots.reduce(
        (best, item) =>
          (item.vehicleCount || 0) > best.count
            ? { count: item.vehicleCount, seats: item.totalLeftSeats }
            : best,
        { count: 0, seats: 0 }
      );
      return peak.seats || 0;
    },
    progressPercent() {
      if (!this.task || !this.task.totalDays) return 0;
      return Math.round((this.task.currentDay / this.task.totalDays) * 100);
    },
    progressStatus() {
      if (!this.task) return null;
      if (this.task.status === 2) return "exception";
      if (this.task.status === 1) return "success";
      return null;
    },
    progressText() {
      if (!this.task) return "";
      if (this.task.status === 0) {
        const day = this.task.dayResults && this.task.dayResults.length
          ? this.task.dayResults[this.task.dayResults.length - 1].date
          : this.param.startDate;
        return `推导中 第 ${this.task.currentDay}/${this.task.totalDays} 天 (${day})...`;
      }
      if (this.task.status === 1) return "推导完成";
      return `推导失败：${this.task.errorMsg || "未知错误"}`;
    }
  },
  mounted() {
    this.updateTableHeight();
    window.addEventListener("resize", this.updateTableHeight);
  },
  beforeDestroy() {
    this.stopPoll();
    window.removeEventListener("resize", this.updateTableHeight);
  },
  watch: {
    showResult() {
      this.$nextTick(this.updateTableHeight);
    },
    tab() {
      this.$nextTick(this.updateTableHeight);
    },
    selectedDayIndex() {
      this.$nextTick(this.updateTableHeight);
    }
  },
  methods: {
    updateTableHeight() {
      this.$nextTick(() => {
        const pageRoot = this.$refs.pageRoot;
        if (!pageRoot) return;
        const tabsContent = pageRoot.querySelector(".el-tabs__content");
        const footerHeight = 28;
        if (tabsContent && tabsContent.clientHeight > 0) {
          this.tableHeight = Math.max(320, tabsContent.clientHeight - footerHeight);
          return;
        }
        const top = pageRoot.getBoundingClientRect().top;
        const bottomGap = 16;
        const tabsHeader = 42;
        this.tableHeight = Math.max(320, window.innerHeight - top - bottomGap - tabsHeader - footerHeight);
      });
    },
    startSimulate() {
      if (!this.param.startDate) {
        this.$message.error("请选择开始推导日期");
        return;
      }
      this.stopPoll();
      this.taskId = null;
      this.task = null;
      this.running = true;
      submitEvSimulate({
        startDate: this.param.startDate,
        days: this.param.days
      }).then((res) => {
        this.taskId = res.data.taskId;
        this.pollTask();
        this.pollTimer = setInterval(() => this.pollTask(), 5000);
      }).catch(() => {
        this.running = false;
      });
    },
    pollTask() {
      if (!this.taskId) return;
      getEvSimulateTask(this.taskId).then((res) => {
        this.task = res.data;
        if (this.task.status === 1) {
          this.selectedDayIndex = Math.max(0, (this.task.dayResults || []).length - 1);
          this.filterPathId = null;
          this.running = false;
          this.stopPoll();
          this.updateTableHeight();
        } else if (this.task.status === 2) {
          this.running = false;
          this.stopPoll();
        }
      });
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    }
  }
};
</script>

<style lang="less" scoped>
.ev-simulate-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 100px);
  padding: 6px 12px 8px;
  box-sizing: border-box;
  overflow: hidden;
}

.top-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  min-height: 32px;
  margin-bottom: 4px;
  flex-shrink: 0;
}

.top-bar__progress {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 200px;
}

.inline-progress {
  width: 120px;
  flex-shrink: 0;
}

.progress-pct {
  font-size: 12px;
  color: #606266;
  min-width: 32px;
}

.status-text {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

.top-bar__summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.summary-tag {
  font-size: 12px;
  color: #606266;
  background: #f0f2f5;
  padding: 2px 8px;
  border-radius: 2px;
  white-space: nowrap;
}

.top-bar__done {
  font-size: 12px;
  color: #67c23a;
}

.top-bar__error {
  font-size: 12px;
  color: #f56c6c;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tabs-with-filter {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.tab-filters {
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;
  height: 40px;
  display: flex;
  align-items: center;
  background: #fff;
  padding-left: 8px;
}

.compact-form {
  /deep/ .el-form-item {
    margin-bottom: 0;
    margin-right: 12px;
  }

  /deep/ .el-form-item__label {
    padding-right: 6px;
    font-size: 12px;
  }
}

.main-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-top: 4px;
}

.fill-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  /deep/ .el-tabs__header {
    margin-bottom: 6px;
    padding-right: 310px;
  }

  /deep/ .el-tabs__content {
    flex: 1;
    min-height: 0;
  }

  /deep/ .el-tab-pane {
    height: 100%;
  }
}
</style>
